import { doc, serverTimestamp, updateDoc } from 'firebase/firestore';

import { deriveScore } from '@/core/engine/x01/game';
import type { GameState, Side } from '@/core/engine/x01/types';
import { DomainError } from '@/core/errors/DomainError';
import type {
  EntryMode,
  Match,
  MatchCounter,
  MatchResult,
  RecordedTurn,
  TeamId,
  Uid,
} from '@/core/schemas';
import { toAppError } from '@/infrastructure/errors/AppError';
import { firestore } from '@/infrastructure/firebase/app';
import { firestorePaths } from '@/infrastructure/firestore/paths';

function matchRef(match: Match) {
  return doc(firestore, firestorePaths.match(match.tournamentId, match.id));
}

function teamIdOf(side: Match['home']): TeamId | null {
  return side.kind === 'team' ? side.teamId : null;
}

/**
 * Starts the live counter for a match: freezes the game configuration, records
 * who throws first, and moves the match to `running` with an empty turn log.
 */
export async function startCounter(input: {
  match: Match;
  startScore: number;
  doubleOut: boolean;
  entryMode: EntryMode;
  startingSide: Side;
  actorUid: Uid;
}): Promise<void> {
  const { match, startScore, doubleOut, entryMode, startingSide, actorUid } = input;

  if (match.home.kind !== 'team' || match.away.kind !== 'team') {
    throw new DomainError('MATCH_NOT_REPORTABLE', 'Die Teilnehmer stehen noch nicht fest.', {
      matchId: match.id,
    });
  }

  const counter: MatchCounter = {
    startScore,
    doubleOut,
    entryMode,
    startingSide,
    startedBy: actorUid,
    turns: [],
  };

  try {
    await updateDoc(matchRef(match), {
      counter,
      status: 'running',
      startedAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
  } catch (error) {
    throw toAppError(error);
  }
}

/**
 * Persists the turn log. Called after every entered or undone turn; the whole
 * log is written because it is the single source of truth for the game state.
 */
export async function saveTurns(input: {
  match: Match;
  turns: readonly RecordedTurn[];
  actorUid: Uid;
}): Promise<void> {
  const { match, turns, actorUid } = input;

  if (match.counter === null) {
    throw new DomainError('MATCH_NOT_REPORTABLE', 'Für dieses Spiel läuft kein Counter.', {
      matchId: match.id,
    });
  }

  try {
    await updateDoc(matchRef(match), {
      counter: { ...match.counter, turns },
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
  } catch (error) {
    throw toAppError(error);
  }
}

function buildResult(match: Match, game: GameState): MatchResult {
  const score = deriveScore(game);
  const winnerSide = score.winner;
  const loserSide: Side | null =
    winnerSide === null ? null : winnerSide === 'home' ? 'away' : 'home';

  return {
    winnerTeamId: winnerSide === null ? null : teamIdOf(match[winnerSide]),
    loserTeamId: loserSide === null ? null : teamIdOf(match[loserSide]),
    score: {
      legs: score.legs,
      ...(score.sets !== null ? { sets: score.sets } : {}),
    },
    source: 'agreement',
    confirmedAt: new Date(),
  };
}

/**
 * Records one captain's confirmation of the final result.
 *
 * When it is the second confirmation, the same write completes the match and
 * sets the result derived from the game — mirroring exactly what the security
 * rules allow (both confirmations present ⇒ `completed`).
 */
export async function confirmResult(input: {
  match: Match;
  side: Side;
  game: GameState;
  actorUid: Uid;
}): Promise<void> {
  const { match, side, game, actorUid } = input;

  if (game.winner === null) {
    throw new DomainError('MATCH_NOT_REPORTABLE', 'Das Spiel ist noch nicht entschieden.', {
      matchId: match.id,
    });
  }

  const confirmation = { uid: actorUid, at: new Date() };
  const otherAlreadyConfirmed =
    side === 'home'
      ? match.confirmations.away !== undefined
      : match.confirmations.home !== undefined;

  const nextConfirmations = { ...match.confirmations, [side]: confirmation };

  try {
    if (otherAlreadyConfirmed) {
      await updateDoc(matchRef(match), {
        confirmations: nextConfirmations,
        status: 'completed',
        result: buildResult(match, game),
        finishedAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        updatedBy: actorUid,
      });
    } else {
      await updateDoc(matchRef(match), {
        confirmations: nextConfirmations,
        updatedAt: serverTimestamp(),
        updatedBy: actorUid,
      });
    }
  } catch (error) {
    throw toAppError(error);
  }
}

/** A captain disputes the result: the match goes to conflict for the director. */
export async function rejectResult(input: { match: Match; actorUid: Uid }): Promise<void> {
  const { match, actorUid } = input;

  try {
    await updateDoc(matchRef(match), {
      status: 'conflict',
      updatedAt: serverTimestamp(),
      updatedBy: actorUid,
    });
  } catch (error) {
    throw toAppError(error);
  }
}
